import React from 'react';
import { Box, Typography } from '@mui/material';
import { PetProfile } from '../../types/onboarding';
import { IntakeForm } from '../IntakeForm';

interface ConfirmDetailsStepProps {
  petData: Partial<PetProfile>;
  onUpdateData: (updates: Partial<PetProfile>) => void;
  onComplete: (petName: string) => void;
  onCancel: () => void;
  isLoading: boolean;
  userLanguage: 'en' | 'zh-CN' | 'es' | 'fr';
}

export const ConfirmDetailsStep: React.FC<ConfirmDetailsStepProps> = ({
  petData,
  onUpdateData,
  onComplete,
  onCancel,
  isLoading,
  userLanguage
}) => {
  // Language-specific content
  const getContent = () => {
    const content = {
      en: {
        title: "Complete Pet Profile ✏️",
        description: "Please fill out the intake form to create your pet's complete profile. These details will help care providers understand your pet's needs.",
        submitButtonText: "Create Pet Profile",
      },
      'zh-CN': {
        title: "完善宠物档案 ✏️",
        description: "请填写登记表以创建您的宠物完整档案。这些细节将帮助护理人员了解您宠物的需求。",
        submitButtonText: "创建宠物资料",
      },
      es: {
        title: "Completar Perfil de Mascota ✏️",
        description: "Por favor complete el formulario de admisión para crear el perfil completo de su mascota. Estos detalles ayudarán a los cuidadores a entender las necesidades de su mascota.",
        submitButtonText: "Crear Perfil de Mascota",
      },
      fr: {
        title: "Compléter le Profil de l'Animal ✏️",
        description: "Veuillez remplir le formulaire d'admission pour créer le profil complet de votre animal. Ces détails aideront les soignants à comprendre ses besoins.",
        submitButtonText: "Créer le Profil de l'Animal",
      }
    };

    return content[userLanguage] || content.en;
  };

  const content = getContent();

  const handleSubmit = async (formData: Partial<PetProfile>) => {
    // Update parent state with all form values
    onUpdateData(formData);
    // Complete onboarding using the name from form
    onComplete(formData.name || '');
  };

  return (
    <Box>
      {/* Header */}
      <Box textAlign="center" mb={4}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
          {content.title}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          {content.description}
        </Typography>
      </Box>

      {/* Pet Photo Display */}
      {petData.profilePhoto && (
        <Box display="flex" justifyContent="center" mb={4}>
          <Box
            component="img"
            src={petData.profilePhoto}
            alt="Pet"
            sx={{
              maxWidth: 200,
              maxHeight: 200,
              borderRadius: '50%',
              boxShadow: 3,
              objectFit: 'cover',
              border: '4px solid',
              borderColor: 'primary.main',
              aspectRatio: '1/1'
            }}
          />
        </Box>
      )}

      {/* Embedded Intake Form */}
      <Box sx={{ maxWidth: 600, mx: 'auto' }}>
        <IntakeForm
          onSubmit={handleSubmit}
          isSubmitting={isLoading}
          initialData={petData}
          embedded={true}
          onCancel={onCancel}
          submitButtonText={content.submitButtonText}
        />
      </Box>
    </Box>
  );
};
